import type { FC } from "react";
import { useStaffMode } from "../../../hooks/StaffModeProvider";

interface RadioButtonProps {
  index: number;
  text: string;
  selected: boolean;
  handleRadioButtonChange: (index: number) => void;
  disabled?: boolean;
  name?: string;
  fontSize?: string;
  radioSize?: string;
  color?: "primary" | "secondary" | "accent";
}

const RadioButton: FC<RadioButtonProps> = ({
  text,
  selected,
  index,
  handleRadioButtonChange,
  disabled,
  name,
  fontSize,
  radioSize,
  color,
}) => {
  const { isStaffMode } = useStaffMode();

  const handleChange = () => {
    handleRadioButtonChange(index);
  };

  const selectedColor = color || (isStaffMode ? "secondary" : "primary");

  const radioSizeClass = radioSize ?? "w-5 h-5 md:w-6 md:h-6 p-[.2rem] md:p-1";
  const fontSizeClass =
    fontSize ??
    "flex-1 text-base md:text-lg leading-8 font-normal not-italic text-gray-font first-letter:uppercase";

  const selectedOutlineClass = {
    accent: "outline-Default",
    secondary: "outline-secondary",
    primary: "outline-primary",
  }[selectedColor];
  const selectedBgClass = {
    accent: "bg-Default",
    secondary: "bg-secondary",
    primary: "bg-primary",
  }[selectedColor];

  const outlineClass = selected ? selectedOutlineClass : "outline-gray-font";
  const bgClass = selected ? selectedBgClass : "bg-none";

  return (
    <div
      onClick={handleChange}
      className={`flex gap-[0.625rem] items-center justify-start cursor-pointer ${disabled ? "pointer-events-none opacity-50" : ""}`}
    >
      <input
        type="radio"
        key={index}
        name={name}
        value={index}
        className="hidden"
        disabled={disabled}
        checked={selected}
        readOnly
      />
      <div
        className={`${radioSizeClass} flex rounded-full outline outline-2 ${outlineClass}`}
      >
        <div className={`h-full w-full rounded-full ${bgClass}`} />
      </div>
      <label
        htmlFor={name}
        className={`${fontSizeClass} cursor-pointer text-gray-font`}
      >
        {text}
      </label>
    </div>
  );
};

export default RadioButton;
